import type {
	FilterGroup,
	FieldValueFilter,
	NumericFieldFilter,
	IntervalFieldFilter,
} from '@atlassian/jira-polaris-domain-view/src/filter/types.tsx';
import {
	createGroupFilterFromIssueTypeName,
	getIssueTypeNameFromGroupFilter,
} from '../../utils/filter-groups.tsx';

export const upsertGroupFilter = <
	T extends FieldValueFilter | NumericFieldFilter | IntervalFieldFilter,
>(
	filterGroups: FilterGroup[],
	filter: T,
	issueTypeName: string | undefined,
): FilterGroup[] => {
	// If there is no group for this issue type, create new based on issue type name
	if (issueTypeName === undefined) {
		if (!filterGroups.find((group) => group.groupFilter === undefined)) {
			// Place "All ideas" group at the beginning
			return [
				{
					groupFilter: undefined,
					filters: [filter],
				},
				...filterGroups,
			];
		}
	} else if (
		!filterGroups.find(
			(group) =>
				group.groupFilter && getIssueTypeNameFromGroupFilter(group.groupFilter) === issueTypeName,
		)
	) {
		// Place new group at the end
		return [
			...filterGroups,
			{
				groupFilter: createGroupFilterFromIssueTypeName(issueTypeName),
				filters: [filter],
			},
		];
	}

	// Otherwise update existing group
	const updatedGroups: FilterGroup[] = filterGroups.map((group) => {
		if (
			group.groupFilter === undefined
				? issueTypeName !== undefined
				: getIssueTypeNameFromGroupFilter(group.groupFilter) !== issueTypeName
		) {
			return group;
		}

		return {
			...group,
			filters:
				group.filters.find((f) => 'field' in f && f.field === filter.field) === undefined
					? // If there is no field filter, add new filter to group
						[...group.filters, filter]
					: // Otherwise update existing filter
						group.filters.map((f) =>
							'field' in f && f.field === filter.field && f.type === filter.type ? filter : f,
						),
		};
	});

	return updatedGroups.filter((group) => group.filters.length > 0);
};
